'use client';

import { useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import { Button } from '../../../components/button';
import { Collapsible } from '../../../components/collapsible';
import { BodyDefault } from '../../../components/typography';
import { SettingsSection } from '../../settings/SettingsSection';
import {
  InternalNetworkOutboundConnectivitySection,
  type InternalNetworkOutboundConnectivitySectionProps,
} from './InternalNetworkOutboundConnectivitySection';
import {
  InternalNetworkSensorEnrollmentsPanel,
  type InternalNetworkSensorEnrollmentsPanelProps,
} from './InternalNetworkSensorEnrollmentsPanel';
import {
  InternalNetworkTunnelEnrollmentsPanel,
  type InternalNetworkTunnelEnrollmentsPanelProps,
} from './InternalNetworkTunnelEnrollmentsPanel';
import { hasActiveSensorEnrollment } from './internal-connection.model';

type InternalNetworkConnectionContentBaseProps = {
  sensorEnrollments: InternalNetworkSensorEnrollmentsPanelProps;
  outboundConnectivity: InternalNetworkOutboundConnectivitySectionProps;
  sensorConnectionProgress?: ReactNode;
};

export type InternalNetworkConnectionContentProps =
  | (InternalNetworkConnectionContentBaseProps & {
      isTunnelModeEnabled: true;
      tunnelEnrollments: InternalNetworkTunnelEnrollmentsPanelProps;
    })
  | (InternalNetworkConnectionContentBaseProps & {
      isTunnelModeEnabled: false;
      tunnelEnrollments?: never;
    });

type SensorConnectionSectionProps = {
  isVisible: boolean;
  children: ReactNode;
};

function SensorConnectionSection({
  isVisible,
  children,
}: SensorConnectionSectionProps) {
  const t = useTranslations(
    'designSystem.networkSettings.internalConnection.sensorConnection',
  );

  if (!isVisible || !children) {
    return null;
  }

  return <SettingsSection title={t('title')}>{children}</SettingsSection>;
}

export function InternalNetworkConnectionContent(
  props: InternalNetworkConnectionContentProps,
) {
  const { sensorEnrollments, outboundConnectivity, sensorConnectionProgress } =
    props;
  const t = useTranslations(
    'designSystem.networkSettings.internalConnection.tunnel',
  );
  const [isSensorFallbackOpen, setIsSensorFallbackOpen] = useState(false);
  const hasActiveEnrollment = hasActiveSensorEnrollment(
    sensorEnrollments.enrollments,
  );
  const sensorEnrollmentsPanel = (
    <InternalNetworkSensorEnrollmentsPanel {...sensorEnrollments} />
  );

  return (
    <VStack gap={8} alignItems="stretch" w="full">
      {props.isTunnelModeEnabled ? (
        <>
          <InternalNetworkTunnelEnrollmentsPanel {...props.tunnelEnrollments} />
          <Collapsible.Root
            open={isSensorFallbackOpen}
            onOpenChange={({ open }) => setIsSensorFallbackOpen(open)}
          >
            <Collapsible.Trigger asChild>
              <Button
                variant="ghost"
                size="sm"
                justifyContent="flex-start"
                px={0}
              >
                <HStack gap={2}>
                  {isSensorFallbackOpen ? (
                    <ArrowDown01Icon size={14} />
                  ) : (
                    <ArrowRight01Icon size={14} />
                  )}
                  <BodyDefault>{t('advancedSensorFallback')}</BodyDefault>
                </HStack>
              </Button>
            </Collapsible.Trigger>
            <Collapsible.Content>
              <VStack gap={8} alignItems="stretch" pt={4}>
                <SensorConnectionSection isVisible={hasActiveEnrollment}>
                  {sensorConnectionProgress}
                </SensorConnectionSection>
                {sensorEnrollmentsPanel}
              </VStack>
            </Collapsible.Content>
          </Collapsible.Root>
        </>
      ) : (
        <>
          <SensorConnectionSection isVisible={hasActiveEnrollment}>
            {sensorConnectionProgress}
          </SensorConnectionSection>
          {sensorEnrollmentsPanel}
        </>
      )}

      <InternalNetworkOutboundConnectivitySection
        outboundEndpoints={outboundConnectivity.outboundEndpoints}
        sensorRelease={
          props.isTunnelModeEnabled ? null : outboundConnectivity.sensorRelease
        }
        isSensorReleaseLoading={
          !props.isTunnelModeEnabled &&
          outboundConnectivity.isSensorReleaseLoading
        }
      />
    </VStack>
  );
}
